import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Modal, Tag } from "antd";
import TableComponent from "../../../components/table/table";
import { get, post, put, del } from "../../../Api/http/httpClient";
import ReservationRequestModal from "../../../components/modals/ReservationRequestModal";

// Importaciones para react-big-calendar y moment en español
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/es"; // Importa la localización de español
import "react-big-calendar/lib/css/react-big-calendar.css";

// Configurar moment para usar español
moment.locale("es");
const localizer = momentLocalizer(moment);

// Objeto de mensajes en español para el calendario
const messages = {
  date: "Fecha",
  time: "Hora",
  event: "Evento",
  allDay: "Todo el día",
  week: "Semana",
  work_week: "Semana laboral",
  day: "Día",
  month: "Mes",
  previous: "Anterior",
  next: "Siguiente",
  yesterday: "Ayer",
  tomorrow: "Mañana",
  agenda: "Agenda",
  noEventsInRange: "No hay eventos en este rango.",
  showMore: (total: number) => `+ Ver más (${total})`,
};

const { confirm } = Modal;

interface ReservationRequestDataProps {
  user_data: any;
}

const ReservationRequest: React.FC<ReservationRequestDataProps> = ({ user_data }) => {
  const [data, setData] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortColumn, setSortColumn] = useState("appointment_datetime");
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);
  const [action, setAction] = useState<any | null>(null);
  const [isManagement, setIsManagement] = useState(false); // Para identificar si es una gestión
  const [formData, setFormData] = useState({
    chat_uuid: "",
    appointment_datetime: "",
    status_request: "",
    comments: "",
    email: "",
    service_or_product: "",
    user_name: "",
  });

  // Estado para forzar la actualización del calendario
  const [calendarKey, setCalendarKey] = useState(0);

  // Función para renderizar el estado con un Tag de antd en español
  const renderStatus = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Tag color="gold">Pendiente</Tag>;
      case "APPROVED":
        return <Tag color="green">Aprobado</Tag>;
      case "CANCELLED":
        return <Tag color="red">Cancelado</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  // Definición de columnas para la tabla  
  const headers_table = {
    chat_uuid: "Chat UUID",
    appointment_datetime: "Fecha y Hora",
    display_status: "Estado",
    comments: "Comentarios",
    email: "Email",
    service_or_product: "Servicio o Producto",
    user_name: "Nombre de Usuario",
    create_date: "Fecha de Creación",
    update_date: "Fecha de Actualización",
  };

  // Función para obtener las solicitudes de reserva de la tabla
  const getReservationRequests = async () => {
    setLoading(true);
    setDeleting(null);
    try {
      const res = await get(
        `reservation-requests?establishment_id=${user_data["custom:establishment_id"]}&page=${page}&page_size=${itemsPerPage}&search=${search}&sort_by=${sortColumn}&order_by=${sortOrder}`
      );

      if (res.answer && res.items) {
        const requests = res.items;
        const formattedItems = requests.map((req: any) => {
          // Tag base según el estado de la reserva
          const baseStatus = req.status_request
            ? renderStatus(req.status_request)
            : <Tag>No proporcionado</Tag>;

          let conflictTag = null;
          // Validamos conflicto solo si la reserva es PENDING o APPROVED
          if (req.status_request === "PENDING" || req.status_request === "APPROVED") {
            // Extraemos la fecha y hora (sin minutos ni segundos) de la reserva
            const reqHour = moment(req.appointment_datetime).format("YYYY-MM-DD HH");
            // Buscamos otra reserva aprobada con la misma fecha y hora
            const hasConflict = requests.some((other: any) => {
              if (other.chat_uuid === req.chat_uuid) return false; // ignorar la misma reserva
              if (other.status_request !== "APPROVED") return false; // solo se comparan con reservas aprobadas
              const otherHour = moment(other.appointment_datetime).format("YYYY-MM-DD HH");
              return reqHour === otherHour;
            });
            if (hasConflict) {
              conflictTag = <Tag color="volcano">Conflicto</Tag>;
            }
          }

          return {
            chat_uuid: req.chat_uuid,
            appointment_datetime: req.appointment_datetime,
            status_request: req.status_request,
            display_status: (
              <>
                {baseStatus} {conflictTag}
              </>
            ),
            comments: req.comments ? req.comments : "No proporcionado",
            email: req.email,
            service_or_product: req.service_or_product,
            user_name: req.user_name,
            create_date: req.create_date
              ? moment(req.create_date).format("DD/MM/YYYY HH:mm")
              : "No proporcionado",
            update_date: req.update_date
              ? moment(req.update_date).format("DD/MM/YYYY HH:mm")
              : "No actualizada",
          };
        });
        setData(formattedItems);
        const totalItems = res.total_items;
        setTotalPages(Math.ceil(totalItems / itemsPerPage));
        // Actualizamos la key para forzar la actualización del calendario
        setCalendarKey(prev => prev + 1);
      } else {
        message.error("No se encontraron solicitudes de reserva.");
      }
    } catch (error) {
      message.error("Error al obtener las solicitudes de reserva.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getReservationRequests();
  }, [page, itemsPerPage, search, sortOrder, sortColumn]);

  // Mapeo de reservas a eventos para el calendario.
  // Se muestran solo las solicitudes aprobadas (status_request === "APPROVED").
  // Se usa la fecha de "appointment_datetime" y se asume una duración de 1 hora.
  const events = data
    .filter((reservation) => reservation.status_request === "APPROVED")
    .map((reservation) => {
      const start = new Date(reservation.appointment_datetime);
      const end = new Date(start.getTime() + 60 * 60 * 1000); // +1 hora
      return {
        title: `${reservation.user_name} - ${reservation.service_or_product}`,
        start,
        end,
        allDay: false,
        resource: reservation,
      };
    });

  // Maneja las acciones: crear, editar, eliminar, gestionar o recargar
  const setActionHandler = (action: { type: string; data?: any }) => {
    if (action.type === "edit") {
      setSelectedRequest(action.data);
      setFormData({
        chat_uuid: action.data.chat_uuid,
        appointment_datetime: action.data.appointment_datetime,
        status_request: action.data.status_request,
        comments: action.data.comments || "",
        email: action.data.email,
        service_or_product: action.data.service_or_product,
        user_name: action.data.user_name,
      });
      setAction(action);
      setIsManagement(false);
      handleOpenModal();
    } else if (action.type === "manage") {
      setSelectedRequest(action.data);
      setFormData({
        chat_uuid: action.data.chat_uuid,
        appointment_datetime: action.data.appointment_datetime,
        status_request: action.data.status_request,
        comments: action.data.comments || "",
        email: action.data.email,
        service_or_product: action.data.service_or_product,
        user_name: action.data.user_name,
      });
      setAction(action);
      setIsManagement(true);
      handleOpenModal();
    } else if (action.type === "delete") {
      setAction(action);
      showDeleteConfirm(action.data);
    } else if (action.type === "create") {
      setFormData({
        chat_uuid: "",
        appointment_datetime: "",
        status_request: "",
        comments: "",
        email: "",
        service_or_product: "",
        user_name: "",
      });
      setAction(action);
      setIsManagement(false);
      handleOpenModal();
    } else if (action.type === "reload") {
      getReservationRequests();
    }
  };

  const showDeleteConfirm = (req: any) => {
    confirm({
      title: "¿Estás seguro de eliminar esta solicitud de reserva?",
      content: `Chat UUID: ${req.chat_uuid}`,
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk() {
        handleDelete(req);
      },
      onCancel() {
        setDeleting(null);
      },
    });
  };

  const handleDelete = async (req: any) => {
    setDeleting(req.chat_uuid);
    try {
      const res = await del("reservation-requests", {
        establishment_id: user_data["custom:establishment_id"],
        chat_uuid: req.chat_uuid,
      });

      if (res && res.answer) {
        message.success("Solicitud de reserva eliminada correctamente.");
        getReservationRequests();
      } else {
        message.error("Error al eliminar la solicitud de reserva.");
      }
    } catch (error) {
      message.error("Error al eliminar la solicitud de reserva.");
    } finally {
      setDeleting(null);
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRequest(null);
    setAction(null);
    setIsManagement(false);
  };

  const handleSave = async () => {
    setModalLoading(true);
    try {
      const res = await post("reservation-requests", {
        establishment_id: user_data["custom:establishment_id"],
        name_establishment: user_data.family_name,
        ...formData,
      });

      if (res && res.answer) {
        message.success("Solicitud de reserva creada correctamente.");
        handleCloseModal();
        getReservationRequests();
      } else {
        message.error("Error al crear la solicitud de reserva.");
      }
    } catch (error) {
      message.error("Error al crear la solicitud de reserva.");
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdate = async () => {
    setModalLoading(true);
    try {
      if (selectedRequest) {
        const res = await put("reservation-requests", {
          establishment_id: user_data["custom:establishment_id"],
          name_establishment: user_data.family_name,
          ...formData,
        });

        if (res && res.answer) {
          message.success("Solicitud de reserva actualizada correctamente.");
          handleCloseModal();
          getReservationRequests();
        } else {
          message.error("Error al actualizar la solicitud de reserva.");
        }
      } else {
        message.error("No se seleccionó ninguna solicitud para actualizar.");
      }
    } catch (error) {
      message.error("Error al actualizar la solicitud de reserva.");
    } finally {
      setModalLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  return (
    <>
      <Row justify="center" style={{ marginTop: "20px" }}>
        <Col span={23} style={{ paddingLeft: "20px", paddingRight: "20px" }}>
          {/* Tabla de reservas */}
          <Card
            bordered={false}
            style={{
              borderRadius: "10px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              padding: "0px",
            }}
          >
            <TableComponent
              data={data}
              headers_table={headers_table}
              loading={loading}
              setSearch={setSearch}
              setAction={setActionHandler}
              totalPages={totalPages}
              setPage={setPage}
              page={page}
              setItemsPerPage={setItemsPerPage}
              itemsPerPage={itemsPerPage}
              setSortOrder={setSortOrder}
              setSortColumn={setSortColumn}
              deleting={deleting}
              showManageAction={true}
              hideEditButton={true}
            />
          </Card>

          {/* Calendario de reservas */}
          <Card
            bordered={false}
            style={{
              borderRadius: "10px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              marginTop: "20px",
              padding: "10px",
            }}
          >
            {/* Se actualiza la key para re-montar el calendario */}
            <Calendar
              key={calendarKey}
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 500 }}
              messages={messages}
              onSelectEvent={(event) => {
                console.log("Evento seleccionado:", event);
              }}
              defaultView="month"
            />
          </Card>
        </Col>
      </Row>

      <ReservationRequestModal
        open={modalOpen}
        onClose={handleCloseModal}
        formData={formData}
        handleChange={handleChange}
        handleSave={handleSave}
        handleUpdate={handleUpdate}
        action={action}
        loading={modalLoading}
        isManagement={isManagement}
      />
    </>
  );
};

export default ReservationRequest;